import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ArrowLeftRight, Loader2 } from "lucide-react";

export default function Transfer() {
  const { t } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState([]);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, c, r] = await Promise.all([api.get("/accounts"), api.get("/currencies"), api.get("/rates")]);
    setAccounts(a.data.filter((x) => x.status !== "closed"));
    setCurrencies(c.data);
    setRates(r.data);
  };
  useEffect(() => { load(); }, []);

  const curMap = Object.fromEntries(currencies.map((c) => [c.code, c]));
  const from = accounts.find((a) => a.id === fromId);
  const to = accounts.find((a) => a.id === toId);

  const rate = useMemo(() => {
    if (!from || !to) return 1;
    if (from.currency_code === to.currency_code) return 1;
    const direct = rates.find((r) => r.from_currency === from.currency_code && r.to_currency === to.currency_code);
    if (direct) return direct.internal_rate;
    const inv = rates.find((r) => r.from_currency === to.currency_code && r.to_currency === from.currency_code);
    if (inv && inv.internal_rate) return 1 / inv.internal_rate;
    return null;
  }, [from, to, rates]);

  const amt = parseFloat(amount) || 0;
  const converted = rate ? amt * rate : 0;
  const fee = from ? (from.currency_code === to?.currency_code ? from.transfer_fee : from.cross_currency_fee) : 0;

  const submit = async () => {
    if (!fromId || !toId) return toast.error(t("tr.pickAccts"));
    if (fromId === toId) return toast.error(t("tr.sameAcct"));
    if (amt <= 0) return toast.error(t("tr.badAmount"));
    if (rate === null) return toast.error(t("tr.noRate"));
    setBusy(true);
    try {
      const { data } = await api.post("/operations/transfer", { from_account_id: fromId, to_account_id: toId, amount: amt });
      toast.success(`${t("tr.done")} · ${fmtMoney(data.credited, curMap[to.currency_code]?.decimals || 2)} ${to.currency_code}`);
      setAmount("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const optLabel = (a) => `${CURRENCY_META[a.currency_code]?.flag || ""} ${a.account_number} · ${fmtMoney(a.available_balance, curMap[a.currency_code]?.decimals || 2)}`;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><ArrowLeftRight className="text-[#00E5FF]" /> {t("tr.title")}</h1>
        <p className="text-zinc-500 text-sm mt-1">{t("tr.sub")}</p>
      </div>

      <div className="ob-card rounded-md p-7 space-y-5">
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("tr.from")}</label>
          <select value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="transfer-from-select"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
            <option value="">{t("tr.pickFrom")}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{optLabel(a)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("tr.to")}</label>
          <select value={toId} onChange={(e) => setToId(e.target.value)} data-testid="transfer-to-select"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
            <option value="">{t("tr.pickTo")}</option>
            {accounts.filter((a) => a.id !== fromId).map((a) => <option key={a.id} value={a.id}>{optLabel(a)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("common.amount")}</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} dir="ltr" placeholder="0.00" data-testid="transfer-amount-input"
            className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] outline-none" />
        </div>

        {from && to && (
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-md p-4 text-sm space-y-2" data-testid="transfer-preview">
            <div className="flex justify-between"><span className="text-zinc-500">{t("tr.rate")}</span><span className="font-mono-ob text-[#00E5FF]" dir="ltr">{rate === null ? "—" : `1 ${from.currency_code} = ${rate} ${to.currency_code}`}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">{t("tr.fee")}</span><span className="font-num text-zinc-300" dir="ltr">{fmtMoney(fee, 2)} {from.currency_code}</span></div>
            <div className="flex justify-between border-t border-zinc-800 pt-2"><span className="text-zinc-400">{t("tr.willReceive")}</span><span className="font-num font-bold text-emerald-400" dir="ltr">{fmtMoney(converted, curMap[to.currency_code]?.decimals || 2)} {to.currency_code}</span></div>
          </div>
        )}

        <button onClick={submit} disabled={busy} data-testid="transfer-submit-button"
          className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t("tr.execute")}
        </button>
      </div>
    </div>
  );
}
